// "Cómo queda una entrega" — simulador en vivo de la configuración que se está editando.
//
// Es el reemplazo real de los párrafos que explicaban la cascada. En vez de contar que
// el monto por defecto se ajusta por comuna, después por tamaño, y que pick & go y las
// flotas son excepciones, la pantalla lo MUESTRA sobre un caso concreto y con el
// desglose de cada paso. La matemática vive aparte y con tests (driverPaymentSimulator.js)
// porque replica el motor de microservice-billing.
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calculator } from 'lucide-react';
import { InfoHint } from '../../../../components/ui/InfoHint';
import { DRIVER_VEHICLE_OPTIONS, driverVehicleLabel } from '../../../../utils/driverVehicleTypes';
import { communesWithRules, simulateDelivery } from './driverPaymentSimulator';

const selectCls =
  'h-8 rounded-md border border-border bg-bg px-2 text-sm w-full focus:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-brand-500';

const SIZE_OPTIONS = ['XL', '2XL', '3XL'];

// Valor centinela del selector de comuna: representa "cualquier comuna sin regla
// propia", que es justamente la pregunta que más se hace (¿cuánto paga el resto?).
const OTHER_COMMUNE = '__other__';

export default function DriverPaymentPreview({
  config,
  currency = 'CLP',
  locale = 'es-CL',
  communeWord = 'Comuna',
  fleetLeaderNames = {},
}) {
  const { t } = useTranslation();
  const [commune, setCommune] = useState(OTHER_COMMUNE);
  const [vehicleType, setVehicleType] = useState('');
  const [sizeCategory, setSizeCategory] = useState('');
  const [pickAndGo, setPickAndGo] = useState(false);
  const [fleetLeaderId, setFleetLeaderId] = useState('');

  const byVehicle = config.paymentMode === 'byVehicle';
  const zoneCommunes = useMemo(() => communesWithRules(config.zones), [config.zones]);
  const fleetLeaders = config.fleetLeaders ?? [];
  // El toggle de modalidad solo cambia el resultado si el operador apagó el ajuste por
  // comuna en pick & go; si no, mostrarlo sería un control que no hace nada.
  const pickAndGoMatters = config.adjustPickAndGoByCommune === false;

  const result = useMemo(
    () =>
      simulateDelivery(config, {
        communeName: commune === OTHER_COMMUNE ? '' : commune,
        vehicleType: vehicleType || null,
        sizeCategory: sizeCategory || null,
        pickAndGo: pickAndGoMatters && pickAndGo,
        fleetLeaderId: fleetLeaderId || null,
      }),
    [config, commune, vehicleType, sizeCategory, pickAndGo, pickAndGoMatters, fleetLeaderId],
  );

  const fmt = (n) => {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'CLP',
        maximumFractionDigits: 0,
      }).format(n || 0);
    } catch {
      return `${currency} ${Math.round(n || 0)}`;
    }
  };
  const signed = (n) => (n > 0 ? `+${fmt(n)}` : fmt(n));

  const stepLabel = (s) => {
    switch (s.key) {
      case 'fleet':
        return t('Monto plano de la flota');
      case 'vehicleBase':
        return t('Monto de {{v}}', { v: driverVehicleLabel(s.vehicleType, t) });
      case 'default':
        return t('Monto por defecto');
      case 'zone':
        return commune === OTHER_COMMUNE ? communeWord : commune;
      case 'pickAndGoNoZone':
        return t('Pick & go: sin ajuste por comuna');
      case 'size':
        return t('Sobredimensionado {{c}}', { c: s.category });
      case 'sizeUnconfigured':
        return t('{{c}} sin recargo configurado', { c: s.category });
      default:
        return s.key;
    }
  };

  const taxLabel =
    config.taxMode === 'inclusive'
      ? t('IVA incluido')
      : t('+ IVA {{p}}%', { p: Math.round((config.taxRate ?? 0.19) * 100) });

  return (
    <div className="rounded-lg border border-border bg-bg-muted p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-brand-50 shrink-0">
          <Calculator className="w-3.5 h-3.5 text-brand-700" aria-hidden="true" />
        </span>
        <h3 className="text-sm font-semibold text-fg m-0">
          {t('Cómo queda una entrega')}
        </h3>
        <InfoHint
          align="end"
          srLabel={t('Cómo se calcula esta estimación')}
          label={t(
            'Simula el pago de una entrega con lo que tienes en pantalla, incluso antes de guardar. No considera las tarifas propias de un repartidor (las de "Tarifas por repartidor"), que le ganan a estas reglas.',
          )}
        />
      </div>

      {/* Escenario */}
      <div className="space-y-2">
        <label className="block">
          <span className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider">
            {communeWord}
          </span>
          <select
            className={`${selectCls} mt-1`}
            value={commune}
            onChange={(e) => setCommune(e.target.value)}
          >
            <option value={OTHER_COMMUNE}>{t('Otra (sin regla propia)')}</option>
            {zoneCommunes.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>

        {byVehicle && (
          <label className="block">
            <span className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider">
              {t('Vehículo')}
            </span>
            <select
              className={`${selectCls} mt-1`}
              value={vehicleType}
              onChange={(e) => setVehicleType(e.target.value)}
            >
              <option value="">{t('Sin vehículo')}</option>
              {DRIVER_VEHICLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {t(o.labelKey)}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="block">
          <span className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider">
            {t('Tamaño')}
          </span>
          <select
            className={`${selectCls} mt-1`}
            value={sizeCategory}
            onChange={(e) => setSizeCategory(e.target.value)}
          >
            <option value="">{t('Normal')}</option>
            {SIZE_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>

        {pickAndGoMatters && (
          <label className="flex items-center gap-2 text-sm text-fg cursor-pointer">
            <input
              type="checkbox"
              checked={pickAndGo}
              onChange={(e) => setPickAndGo(e.target.checked)}
            />
            {t('Retiro pick & go')}
          </label>
        )}

        {fleetLeaders.length > 0 && (
          <label className="block">
            <span className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider">
              {t('Flota')}
            </span>
            <select
              className={`${selectCls} mt-1`}
              value={fleetLeaderId}
              onChange={(e) => setFleetLeaderId(e.target.value)}
            >
              <option value="">{t('Repartidor sin flota')}</option>
              {fleetLeaders.map((f) => (
                <option key={f.driverId} value={f.driverId}>
                  {fleetLeaderNames[f.driverId] || f.driverId}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* Resultado */}
      <div className="border-t border-border pt-3">
        {!result.priced ? (
          <p className="text-sm text-status-warning m-0">
            {t('Falta el monto por defecto para poder calcular.')}
          </p>
        ) : (
          <>
            {/* `role="status"` + aria-live: la cifra cambia al mover el escenario y es
                el resultado que el usuario vino a buscar, así que un lector de pantalla
                tiene que anunciarla sin que haga falta navegar hasta ella. */}
            <p
              role="status"
              aria-live="polite"
              className="text-2xl font-semibold text-fg tabular-nums m-0"
            >
              {fmt(result.unit)}
            </p>
            <ul className="mt-2 space-y-1 list-none m-0 p-0">
              {result.steps.map((s, i) => (
                <li
                  key={`${s.key}-${i}`}
                  className="flex items-baseline justify-between gap-2 text-xs"
                >
                  <span className="text-fg-muted truncate">{stepLabel(s)}</span>
                  <span
                    className={
                      'tabular-nums shrink-0 ' +
                      (s.key === 'sizeUnconfigured' ? 'text-status-warning' : 'text-fg')
                    }
                  >
                    {i === 0 ? fmt(s.amount) : signed(s.amount)}
                  </span>
                </li>
              ))}
              <li className="flex items-baseline justify-between gap-2 text-xs border-t border-border/60 pt-1 mt-1">
                <span className="text-fg-muted">{taxLabel}</span>
                <span className="text-fg tabular-nums">{fmt(result.total)}</span>
              </li>
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
